import { useMemo } from 'react';
import { ISalesTeam } from '@/data/models/salesTerritory';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { ICurrentPricingChangesGroup } from '@/types/product-current-changes-types';

interface UsePricingCurrentChangesParams {
  currentChanges?: ICurrentPricingChangesGroup;
  allSalesTeams: ISalesTeam[];
  currentPrimarySalesTeamId?: number | string | null;
}

export const usePricingCurrentChanges = ({
  currentChanges,
  allSalesTeams,
  currentPrimarySalesTeamId,
}: UsePricingCurrentChangesParams) => {
  const hasCurrentChanges = Boolean(currentChanges);

  // Finds the current change entry that belongs to a specific sales team.
  const getPricingChangeBySalesTeamId = (
    salesTeamId?: number | string | null
  ) => {
    if (!hasCurrentChanges || salesTeamId == null) return undefined;

    return currentChanges?.albumSalesTeams.find(
      (change) => Number(change.salesTeamId) === Number(salesTeamId)
    );
  };

  const getSalesTeamById = (salesTeamId?: number | string | null) => {
    if (salesTeamId == null) return undefined;

    return allSalesTeams.find(
      (salesTeam) => Number(salesTeam.id) === Number(salesTeamId)
    );
  };

  // Resolves a sales team display name from either active or inactive lists.
  const getSalesTeamName = (salesTeamId?: number | string | null) => {
    return getSalesTeamById(salesTeamId)?.salesTeamName ?? null;
  };

  // Formats the original price for the change indicator tooltip.
  const getOriginalPriceDisplayValue = (
    price: unknown,
    currencyName?: string
  ): string | null | undefined => {
    if (price === null || price === undefined) return price;

    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return String(price);

    const currencySymbol = getCurrencySymbol(currencyName);
    return `${currencySymbol}${currencySymbol ? ' ' : ''}${numericPrice.toFixed(2)}`;
  };

  // Finds the sales team originally selected as primary.
  const originalPrimarySalesTeamChange = useMemo(() => {
    if (!hasCurrentChanges) return undefined;

    return currentChanges?.albumSalesTeams.find(
      (change) => change.isPrimary.hasChanges && change.isPrimary.value === true
    );
  }, [currentChanges, hasCurrentChanges]);

  // Resolves the price change for the original primary sales team.
  const originalPrimaryPriceChange = useMemo(() => {
    if (!hasCurrentChanges) return undefined;

    return getPricingChangeBySalesTeamId(
      originalPrimarySalesTeamChange?.salesTeamId ?? currentPrimarySalesTeamId
    );
  }, [
    currentChanges,
    hasCurrentChanges,
    originalPrimarySalesTeamChange,
    currentPrimarySalesTeamId,
  ]);

  // Keeps the original primary sales team object to read currency metadata.
  const originalPrimarySalesTeam = getSalesTeamById(
    originalPrimaryPriceChange?.salesTeamId
  );

  // Stores the translated name shown as the original primary sales team value.
  const originalPrimarySalesTeamName = getSalesTeamName(
    originalPrimarySalesTeamChange?.salesTeamId
  );

  return {
    hasCurrentChanges,
    getPricingChangeBySalesTeamId,
    getSalesTeamById,
    getSalesTeamName,
    getOriginalPriceDisplayValue,
    originalPrimarySalesTeamChange,
    originalPrimaryPriceChange,
    originalPrimarySalesTeam,
    originalPrimarySalesTeamName,
  };
};
